import { MotionProps, motion } from "framer-motion";
import { useContext, useMemo } from "react";
import { Popover, PopoverProps } from "react-aria-components";
import { MenuTriggerContext } from "./types";
import { tv } from "tailwind-variants";

const styles = tv({
  base: "bg-white shadow-y-plus-3 rounded-4 p-2 outline-hidden",
});

const MotionPopover = motion(Popover);

type Placement =
  | "bottom"
  | "bottom left"
  | "bottom right"
  | "bottom start"
  | "bottom end"
  | "top"
  | "top left"
  | "top right"
  | "top start"
  | "top end"
  | "left"
  | "left top"
  | "left bottom"
  | "start"
  | "start top"
  | "start bottom"
  | "right"
  | "right top"
  | "right bottom"
  | "end"
  | "end top"
  | "end bottom";

export default function MenuPopover({
  placement = "bottom",
  ...props
}: PopoverProps & MotionProps) {
  const { currentAnimation, onComplete } = useContext(MenuTriggerContext);
  const container = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    return document.getElementById("storybook-root") ?? document.body;
  }, []);
  return (
    <MotionPopover
      className={styles()}
      UNSTABLE_portalContainer={container}
      variants={{
        hidden(placement: Placement) {
          let x = 0;
          if (placement.includes("left")) {
            x = -30;
          } else if (placement.includes("right")) {
            x = 30;
          }

          let y = 0;
          if (placement.includes("top")) {
            y = 30;
          } else if (placement.includes("bottom")) {
            y = -30;
          }

          return {
            opacity: 0,
            scale: 0.95,
            x,
            y,
            pointerEvents: "none",
          };
        },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          x: 0,
          pointerEvents: "none",
          transitionEnd: {
            pointerEvents: "auto",
          },
        },
      }}
      initial="hidden"
      custom={placement}
      placement={placement}
      animate={currentAnimation}
      transition={{ type: "spring", mass: 0.2, stiffness: 150 }}
      isExiting={currentAnimation === "hidden"}
      onAnimationComplete={onComplete}
      {...props}
    />
  );
}
